import { getFormProps } from '@conform-to/react';
import {
  Form as RouterForm,
  type FormProps as RouterFormProps,
  useSearchParams,
  useSubmit,
} from 'react-router';
import {
  HoneypotInputs,
  HoneypotProvider as RemixHoneypotProvider,
} from 'remix-utils/honeypot/react';

import {
  type useCustomFetcher,
  useDelayedIsPending,
} from '@veraclins-dev/react-utils';
import { Box } from '@veraclins-dev/ui/box';
import { ErrorList } from '@veraclins-dev/ui/error-list';
import { cn } from '@veraclins-dev/utils';

import { type useConform } from '../hooks/use-conform';

import { FormSubmitButton } from './submit-button';

/**
 * Props for {@link Form}. Layers Conform field/error wiring, honeypot
 * inputs, and a default submit button on top of React Router's `Form`.
 */
interface FormProps extends Omit<RouterFormProps, 'method' | 'onSubmit'> {
  /**
   * HTTP method. Defaults to `'post'`. With `'get'` the field values become
   * search params on the action URL, and the honeypot and `redirectTo` inputs
   * are not rendered so they stay out of the URL. A GET form ignores
   * `redirectTo` and `redirectToField` and renders no honeypot, so its target
   * loader must not change data.
   */
  method?: 'post' | 'get';
  /** Label for the default submit button. Ignored when `actionButtons` is set. */
  submitText?: React.ReactNode;
  /**
   * Replace the default submit button with custom action UI. In a GET form,
   * give a `FormSubmitButton` here `method="get"`, or it shows no pending state.
   */
  actionButtons?: React.ReactNode;
  /** Explicit post-submit redirect target. Overrides the URL search param. */
  redirectTo?: string | null;
  /** Search-param key to read for a redirect target. Defaults to `redirectTo`. */
  redirectToField?: string;
  /** Result of {@link useConform}, narrowed to its `form` handle. */
  form: ReturnType<typeof useConform>['form'];
  /** Optional {@link useCustomFetcher} to submit through a fetcher instead of a navigation. */
  fetcher?: ReturnType<typeof useCustomFetcher>;
  disabled?: boolean;
  /** Suppress the default submit button entirely. */
  noButtons?: boolean;
  /** Suppress the form-level error list. */
  noError?: boolean;
  contentClassName?: string;
}

/**
 * Conform-wired form wrapper. POSTs to the current route by default,
 * renders honeypot inputs, surfaces form-level errors, and supplies a
 * default submit button with delayed-pending state.
 *
 * Pass a `fetcher` to submit via React Router's fetcher API rather than
 * a full navigation. Wrap with `HoneypotProvider` in your app root to
 * enable the honeypot inputs.
 *
 */
const Form = ({
  children,
  submitText = 'Submit',
  actionButtons,
  form,
  className,
  redirectTo,
  disabled,
  noButtons,
  noError,
  fetcher,
  redirectToField = 'redirectTo',
  contentClassName,
  method = 'post',
  ...props
}: FormProps) => {
  const isPending = useDelayedIsPending({
    formAction: props.action,
    formMethod: method === 'get' ? 'GET' : 'POST',
  });
  const [searchParams] = useSearchParams();
  const submit = useSubmit();

  const Comp = fetcher ? fetcher.Form : RouterForm;

  const isGet = method === 'get';

  const loading = fetcher?.loading ?? isPending;

  const formProps = getFormProps(form);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    // Let Conform validate. On a validation failure (or never for INTENT
    // submitters) it calls preventDefault; on a valid submit it does not.
    formProps.onSubmit?.(event);

    const submitter = (event.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement | null;
    // Conform's INTENT submitters (validate/update/reset/array ops) are
    // client-only — they must never reach the server.
    if (submitter?.name === '__intent__') {
      event.preventDefault();
      return;
    }

    // Conform blocked the submit (validation failure) — nothing to send.
    if (event.defaultPrevented) return;

    // Own the submission. React Router is the only real submitter here
    // (Conform only validates), so we always preventDefault and issue
    // exactly one submission ourselves. Leaving the default un-prevented
    // let a second submission path run alongside RR's under RR 7.16+
    // (`v8_middleware`), firing a duplicate POST per click.
    event.preventDefault();
    const target = submitter ?? event.currentTarget;
    if (fetcher) {
      fetcher.submit(target, { method });
    } else {
      submit(target, {
        method,
        replace: props.replace,
        state: props.state,
        preventScrollReset: props.preventScrollReset,
        viewTransition: props.viewTransition,
        relative: props.relative,
      });
    }
  };

  return (
    <Comp
      method={method}
      className={cn(
        'flex w-full flex-col items-center gap-y-4 lg:gap-y-6',
        className,
      )}
      {...props}
      {...formProps}
      onSubmit={handleSubmit}
    >
      {isGet ? null : (
        <>
          <input
            type="hidden"
            name="redirectTo"
            value={redirectTo || searchParams.get(redirectToField) || undefined}
          />
          <HoneypotInputs />
        </>
      )}
      <fieldset
        className={cn(
          'flex h-full w-full min-w-0 flex-col gap-y-4',
          contentClassName,
        )}
        disabled={disabled}
      >
        {children}
      </fieldset>
      {!noError ? (
        <Box
          display="flex"
          items="center"
          // Keeps the alert region mounted but out of flow until errors arrive.
          className={cn(form.errors?.length ? 'py-1' : 'absolute')}
        >
          <ErrorList errors={form.errors} id={form.errorId} urgent />
        </Box>
      ) : null}

      {!noButtons &&
        (actionButtons ?? (
          <FormSubmitButton
            w="full"
            px={{ xs: 6, sm: 12 }}
            py={3}
            className="min-w-[200px] mt-auto"
            loading={loading}
            disabled={disabled}
            action={props.action}
            method={method}
          >
            {submitText}
          </FormSubmitButton>
        ))}
    </Comp>
  );
};

/**
 * Re-export of remix-utils' honeypot provider. Wrap your app root with it so
 * {@link Form}'s `HoneypotInputs` have field config to render — without it the
 * honeypot inputs render nothing and the spam guard is inert. Pages behind a
 * session may omit it when their action passes `skipHoneypot` to
 * `processForm`.
 *
 * @subcomponentOf Form
 */
const HoneypotProvider = RemixHoneypotProvider;

export { Form, type FormProps, HoneypotProvider };
